'use client';

import * as RadixToast from '@radix-ui/react-toast';
import { CSSProperties, useEffect, useState } from 'react';
import { feedbackStateIconsMap } from '../../models/feedback-state-icons.const';
import { feedbackState } from '../../models/feedback-state.const';
import { Button, ButtonProps } from '../Button';
import { Icon } from '../Icon';
import { Stack } from '../layout/item';
import { Text } from '../typography';
import styles from './toast.module.scss';

export const toastFeedbackState = [...feedbackState, 'none'] as const;
type ToastFeedbackState = (typeof toastFeedbackState)[number];

type ToastProps = {
  isOpen: boolean;
  onClose?: () => void;
  feedbackState?: ToastFeedbackState;
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
} & RadixToast.ToastProps;

const FIXED_DURATION = 1000 * 60 * 10;

/**
 * Toast component
 *
 * @requires ToastProvider
 *
 * @param isOpen Boolean
 * @param onClose Callback function
 * @param feedbackState Toast variant
 * @param title String
 * @param description String
 * @param action If provided, the action will be rendered as a button. Label and onClick function are required.
 * @param duration Duration in milliseconds (ms). If provided, the toast will be closed on timeout.
 * @param props RadixToast.ToastProps
 */
export const Toast = ({
  isOpen,
  onClose,
  feedbackState = 'none',
  title,
  description,
  action,
  duration,
  ...props
}: ToastProps) => {
  const [open, setOpen] = useState(false);

  const handleOpenChange = (openState: boolean) => {
    setOpen(openState);
    if (!openState) onClose?.();
  };

  useEffect(() => setOpen(isOpen), [isOpen]);

  const getButtonVariant = (
    state: ToastFeedbackState
  ): {
    variant: ButtonProps['variant'];
    state: ButtonProps['state'];
  } => {
    switch (state) {
      case 'info':
      case 'warn':
        return { variant: 'tertiary', state: 'neutral' };
      case 'error':
        return { variant: 'primary', state: 'warning' };
      case 'success':
        return { variant: 'primary', state: 'success' };
      default:
        return { variant: 'primary', state: 'neutral' };
    }
  };

  return (
    <>
      <RadixToast.Root
        open={open}
        defaultOpen={false}
        onOpenChange={handleOpenChange}
        duration={duration ?? FIXED_DURATION}
        data-feedback-state={feedbackState}
        className={styles.toast}
        style={
          { '--toast-progress-animation-duration': duration } as CSSProperties
        }
        {...props}
      >
        <Stack
          as={'header'}
          direction={'h'}
          gap={'3xs'}
          className={styles.header}
        >
          {feedbackState !== 'none' && (
            <div className={styles.icon}>
              <Icon name={feedbackStateIconsMap[feedbackState]} />
            </div>
          )}

          {title && (
            <RadixToast.Title className={styles.title}>
              <Text size={'l'}>
                <strong>{title}</strong>
              </Text>
            </RadixToast.Title>
          )}

          <RadixToast.Close className={styles.closeButton}>
            <Icon name={'x'} size={'xs'} />
          </RadixToast.Close>
        </Stack>

        {(description || action) && (
          <Stack gap={'xs'} className={styles.content}>
            {description && (
              <RadixToast.Description asChild>
                <Text>{description}</Text>
              </RadixToast.Description>
            )}

            {action && (
              <RadixToast.Action
                asChild
                altText={'action'}
                className={styles.action}
              >
                <Button
                  {...getButtonVariant(feedbackState)}
                  className={styles.action}
                  size={'s'}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              </RadixToast.Action>
            )}
          </Stack>
        )}

        {!!duration && (
          <div className={styles.progressContainer}>
            <div className={styles.progress} />
          </div>
        )}
      </RadixToast.Root>
    </>
  );
};
